import { useEffect, useRef, useCallback, useState } from 'react';
import { client, DATABASE_ID } from '../lib/appwrite';

/**
 * Custom hook for managing Appwrite real-time subscriptions
 *
 * @param {Object} options - Configuration options
 * @param {string|string[]} options.channels - Channel(s) to subscribe to
 * @param {Function} options.onEvent - Callback when event is received
 * @param {boolean} options.enabled - Whether subscription is enabled (default: true)
 * @param {number} options.debounce - Debounce time in ms (default: 0)
 * @returns {Object} - { isConnected, error, reconnect }
 */
const useRealtimeSubscription = ({ channels, onEvent, enabled = true, debounce = 0 }) => {
  const subscriptionRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const handleEvent = useCallback((response) => {
    if (!response || !response.events || response.events.length === 0) return;
    const event = response.events[0];
    const payload = response.payload;
    if (debounce > 0) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onEvent({ event, payload, response });
      }, debounce);
    } else {
      onEvent({ event, payload, response });
    }
  }, [onEvent, debounce]);

  const unsubscribe = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (subscriptionRef.current) {
      try { subscriptionRef.current(); } catch (_) {}
      subscriptionRef.current = null;
    }
    if (mountedRef.current) setIsConnected(false);
  }, []);

  const subscribe = useCallback(() => {
    if (!enabled || !channels || !mountedRef.current) return;

    // Clean up any existing subscription first
    if (subscriptionRef.current) {
      try { subscriptionRef.current(); } catch (_) {}
      subscriptionRef.current = null;
    }

    try {
      const channelArray = Array.isArray(channels) ? channels : [channels];
      subscriptionRef.current = client.subscribe(channelArray, handleEvent);
      retryCountRef.current = 0;
      if (mountedRef.current) {
        setIsConnected(true);
        setError(null);
      }
    } catch (err) {
      // WebSocket may still be in CONNECTING state — retry with exponential backoff
      const isConnectingError =
        err instanceof DOMException ||
        (err.message && err.message.includes('CONNECTING'));

      if (isConnectingError && retryCountRef.current < 5 && mountedRef.current) {
        const delay = Math.min(1000 * 2 ** retryCountRef.current, 16000);
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(subscribe, delay);
      } else {
        if (mountedRef.current) {
          setError(err);
          setIsConnected(false);
        }
      }
    }
  }, [channels, enabled, handleEvent]);

  const reconnect = useCallback(() => {
    retryCountRef.current = 0;
    unsubscribe();
    retryTimerRef.current = setTimeout(subscribe, 500);
  }, [subscribe, unsubscribe]);

  useEffect(() => {
    mountedRef.current = true;
    // Delay initial subscription slightly to let the WebSocket open
    retryTimerRef.current = setTimeout(subscribe, 300);
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-subscribe when channels or enabled changes
  useEffect(() => {
    if (mountedRef.current) {
      retryCountRef.current = 0;
      unsubscribe();
      retryTimerRef.current = setTimeout(subscribe, 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(channels), enabled]);

  return { isConnected, error, reconnect };
};

/**
 * Helper to create channel strings for tables
 */
export const createTableChannel = (tableId) => {
  return `databases.${DATABASE_ID}.tables.${tableId}.rows`;
};

/**
 * Helper to detect event types
 */
export const getEventType = (event) => {
  if (event.includes('create')) return 'create';
  if (event.includes('update')) return 'update';
  if (event.includes('delete')) return 'delete';
  return 'unknown';
};

export default useRealtimeSubscription;
